/**
 * The page "Extraction training" (4 Nov 2026): the TanStack Query hook over
 * `GET /extraction-training` (`getExtractionTraining`).
 *
 * How well I2Q finds the customer, the items and the quantity, measured against what
 * sales quoted and what was ordered, per area and per ISO week, with the gaps and their
 * cases. The route needs the capability `see_sales_truth`, like "Learn from sales"; the
 * server answers 403 without it and binds every row to the areas of the session. The
 * first part of the query key is the request key of the usage vocabulary.
 */

import { useQuery } from "@tanstack/react-query";

import {
  type ExtractionTraining,
  getExtractionTraining,
  type GetExtractionTrainingData,
  type TrainingGap,
} from "./generated";

export type {
  ExtractionTraining,
  TrainingArea,
  TrainingCase,
  TrainingGap,
  TrainingGapCount,
  TrainingRates,
  TrainingWeek,
} from "./generated";

/** The kinds of gap, in the order of the page (`sales_truth.training.GAP_KINDS`). */
export type TrainingGapKind = TrainingGap["kind"];

/** The filters of the page (every one optional; the server has the defaults). */
export type ExtractionTrainingQuery = NonNullable<GetExtractionTrainingData["query"]>;

export const extractionTrainingKey = (query: ExtractionTrainingQuery) =>
  ["extraction-training", query] as const;

/** The read changes at most with each ingest run. */
const STALE_MS = 60_000;

/** The page: the rates, the weeks, the gaps and the cases of the period. */
export function useExtractionTraining(query: ExtractionTrainingQuery, options = { enabled: true }) {
  return useQuery({
    queryKey: extractionTrainingKey(query),
    queryFn: async (): Promise<ExtractionTraining | null> => {
      const { data } = await getExtractionTraining({ query, throwOnError: true });
      return data ?? null;
    },
    enabled: options.enabled,
    retry: false,
    staleTime: STALE_MS,
  });
}
